import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  EyeIcon,
  KeyIcon,
  PowerIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { FormField, Input, Select } from '../../components/Forms';
import { User } from '../../types';
import UserForm, { UserFormMode } from './UserForm';
import ResetPasswordModal from './ResetPasswordModal';

interface UserFilters {
  search: string;
  role: string;
  isActive: string;
}

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    isActive: '',
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<UserFormMode>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');

  const { data: usersData, isLoading, error } = useQuery({
    queryKey: ['users', currentPage, filters],
    queryFn: () =>
      apiService.getUsers(currentPage, 10, {
        search: filters.search,
        role: filters.role,
        isActive: filters.isActive,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => apiService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setUserToDelete(null);
      setActionMessage('تم حذف المستخدم نهائياً');
      setActionError('');
    },
    onError: (err: any) => {
      setActionError(
        err?.response?.data?.message || err?.message || 'فشل حذف المستخدم'
      );
      setActionMessage('');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (userId: string) => apiService.toggleUserStatus(userId),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setActionMessage(
        user.isActive ? 'تم تفعيل المستخدم بنجاح' : 'تم إيقاف المستخدم بنجاح'
      );
      setActionError('');
    },
    onError: (err: any) => {
      setActionError(
        err?.response?.data?.message || err?.message || 'فشل تغيير حالة المستخدم'
      );
      setActionMessage('');
    },
  });

  const handleFilterChange = (field: keyof UserFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const openCreate = () => {
    setSelectedUser(null);
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = (user: User) => {
    setSelectedUser(user);
    setFormMode('edit');
    setFormOpen(true);
  };

  const openView = (user: User) => {
    setSelectedUser(user);
    setFormMode('view');
    setFormOpen(true);
  };

  const openReset = (user: User) => {
    setSelectedUser(user);
    setResetOpen(true);
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
  };

  const confirmDelete = () => {
    if (!userToDelete) return;
    deleteMutation.mutate(userToDelete._id);
  };

  const handleToggle = (user: User) => {
    toggleMutation.mutate(user._id);
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: { [key: string]: string } = {
      admin: 'مدير النظام',
      operations: 'عمليات',
      hotel_rep: 'مندوب فندق',
      accounting: 'محاسبة',
      super_admin: 'مدير أعلى',
    };
    return roleLabels[role] || role;
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: { [key: string]: string } = {
      admin: 'bg-red-100 text-red-800',
      operations: 'bg-green-100 text-green-800',
      hotel_rep: 'bg-teal-100 text-teal-800',
      accounting: 'bg-yellow-100 text-yellow-800',
      super_admin: 'bg-red-100 text-red-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatLastLogin = (lastLogin?: Date) => {
    if (!lastLogin) return 'لم يسجل دخول من قبل';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'منذ أقل من ساعة';
    if (diffInHours < 24) return `منذ ${diffInHours} ساعة`;
    if (diffInHours < 48) return 'منذ يوم واحد';
    return `منذ ${Math.floor(diffInHours / 24)} أيام`;
  };

  const users = usersData?.data || [];
  const rawPagination = usersData?.pagination as any;
  const pagination = {
    current: rawPagination?.current ?? rawPagination?.page ?? 1,
    pages: rawPagination?.pages ?? 1,
    total: rawPagination?.total ?? 0,
    limit: rawPagination?.limit ?? 10,
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">حدث خطأ في تحميل البيانات</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">المستخدمين</h1>
          <p className="text-gray-600">إدارة مستخدمي النظام والصلاحيات — بما فيها موظفو بوابة الصعود</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          <PlusIcon className="w-5 h-5 ml-2" />
          إضافة مستخدم جديد
        </button>
      </div>

      {(actionMessage || actionError) && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            actionError
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-green-50 border-green-200 text-green-800'
          }`}
        >
          {actionError || actionMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FormField label="البحث">
            <div className="relative">
              <Input
                placeholder="البحث بالاسم أو اسم المستخدم..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pr-10"
              />
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </FormField>

          <FormField label="الدور">
            <Select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              options={[
                { value: '', label: 'جميع الأدوار' },
                { value: 'admin', label: 'مدير النظام' },
                { value: 'operations', label: 'عمليات' },
                { value: 'hotel_rep', label: 'مندوب فندق' },
                { value: 'accounting', label: 'محاسبة' },
              ]}
            />
          </FormField>

          <FormField label="الحالة">
            <Select
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
              options={[
                { value: '', label: 'جميع الحالات' },
                { value: 'true', label: 'نشط' },
                { value: 'false', label: 'غير نشط' },
              ]}
            />
          </FormField>

          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setFilters({ search: '', role: '', isActive: '' });
                setCurrentPage(1);
              }}
              className="btn-secondary w-full"
            >
              مسح الفلاتر
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  المستخدم
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الدور
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  معلومات الاتصال
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  آخر تسجيل دخول
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                      <span className="mr-3 text-gray-600">جاري التحميل...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    لا توجد مستخدمين مطابقين للبحث
                  </td>
                </tr>
              ) : (
                users.map((user: User) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <UserIcon className="h-6 w-6 text-primary-600" />
                          </div>
                        </div>
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(
                          user.role
                        )}`}
                      >
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        {user.phone && <div className="text-sm text-gray-900">{user.phone}</div>}
                        {user.email && <div className="text-sm text-gray-500">{user.email}</div>}
                        {!user.phone && !user.email && (
                          <span className="text-gray-400">غير محدد</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatLastLogin(user.lastLogin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <button
                          type="button"
                          className="text-primary-600 hover:text-primary-900"
                          title="عرض التفاصيل"
                          onClick={() => openView(user)}
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-900"
                          title="تعديل"
                          onClick={() => openEdit(user)}
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          className={
                            user.isActive
                              ? 'text-green-600 hover:text-green-800'
                              : 'text-gray-400 hover:text-green-600'
                          }
                          title={user.isActive ? 'إيقاف المستخدم' : 'تفعيل المستخدم'}
                          onClick={() => handleToggle(user)}
                          disabled={toggleMutation.isPending}
                        >
                          <PowerIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          className="text-purple-600 hover:text-purple-900"
                          title="إعادة تعيين كلمة المرور"
                          onClick={() => openReset(user)}
                        >
                          <KeyIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(user)}
                          className="text-red-600 hover:text-red-900"
                          title="حذف نهائي"
                          disabled={deleteMutation.isPending}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                السابق
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.pages))}
                disabled={currentPage === pagination.pages}
                className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                التالي
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  عرض <span className="font-medium">{(currentPage - 1) * 10 + 1}</span> إلى{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 10, pagination.total)}
                  </span>{' '}
                  من <span className="font-medium">{pagination.total}</span> نتيجة
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    السابق
                  </button>

                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        type="button"
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.pages))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    التالي
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <UserForm
        isOpen={formOpen}
        mode={formMode}
        user={selectedUser}
        onClose={() => {
          setFormOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          setActionMessage(
            formMode === 'create' ? 'تم إنشاء المستخدم بنجاح' : 'تم تحديث المستخدم بنجاح'
          );
          setActionError('');
        }}
      />

      <ResetPasswordModal
        isOpen={resetOpen}
        user={selectedUser}
        onClose={() => {
          setResetOpen(false);
          setSelectedUser(null);
        }}
      />

      {userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !deleteMutation.isPending && setUserToDelete(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 rounded-full bg-red-100 p-2">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">تأكيد الحذف</h3>
                <p className="mt-2 text-sm text-gray-600">
                  هل أنت متأكد من حذف المستخدم{' '}
                  <span className="font-medium text-gray-900">
                    {userToDelete.fullName}
                  </span>{' '}
                  (@{userToDelete.username}) نهائياً؟
                </p>
                <p className="mt-1 text-sm text-red-600">
                  لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={deleteMutation.isPending}
                onClick={() => setUserToDelete(null)}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                disabled={deleteMutation.isPending}
                onClick={confirmDelete}
              >
                {deleteMutation.isPending ? 'جاري الحذف...' : 'تأكيد الحذف'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
